import { Component, lazy, Suspense } from 'react';

import { Button } from '@primereact/ui/button';
import { useEsStrings } from './EsStrings';

/*
 * A page in its own chunk, and what happens when that chunk is gone.
 *
 * A deploy replaces every chunk hash. A tab opened before it still holds the old index.html's
 * names, so the next lazy page it opens asks for a file the new image does not serve, and the
 * dynamic import rejects. Uncaught, that rejection reached the root errorElement, which replaces
 * the whole shell with EsErrorPage and offers no way back but the address bar.
 *
 * So a chunk that fails to load reloads the page once, which fetches the new index.html and the
 * chunks it names. The guard is a timestamp in sessionStorage, not a flag cleared on success: a
 * page that loads chunk A and fails on chunk B would clear a flag on A and reload forever. Within
 * GUARD_MS of a reload, a second failure draws EsChunkUpdated instead, inside the shell. With no
 * sessionStorage there is no guard, so there is no reload either: the message comes at once.
 *
 * Any other import error -- a module that throws while it evaluates -- is not a deploy, a reload
 * would not mend it, and it goes to the router's errorElement as before.
 */

export const CHUNK_RELOADED_KEY = 'es-chunk-reloaded';
const GUARD_MS = 5 * 60 * 1000;

// The reload, on an object so a test can replace it: jsdom's location.reload cannot be spied on.
export const esChunkPage = { reload: () => window.location.reload() };

/** True for webpack's ChunkLoadError, its CSS twin, and each browser's failed dynamic import. */
export function isChunkLoadError( pError ) {
    if ( !pError ) return false;
    if ( pError.name === 'ChunkLoadError' || pError.code === 'CSS_CHUNK_LOAD_FAILED' ) return true;
    const aMessage = String( pError.message ?? '' );
    return /Loading (CSS )?chunk \S+ failed/i.test( aMessage )
        || /Failed to fetch dynamically imported module/i.test( aMessage )
        || /error loading dynamically imported module/i.test( aMessage )
        || /Importing a module script failed/i.test( aMessage );
}

// Reloads unless one already ran within GUARD_MS; true when it did.
function reloadOnce() {
    try {
        const aAt = Number( window.sessionStorage.getItem( CHUNK_RELOADED_KEY ) );
        if ( aAt && Date.now() - aAt < GUARD_MS ) return false;
        window.sessionStorage.setItem( CHUNK_RELOADED_KEY, String( Date.now() ) );
    } catch {
        return false;
    }
    esChunkPage.reload();
    return true;
}

/**
 * An import() for React's lazy(): a chunk-load failure reloads once and stays pending meanwhile,
 * so the shell holds still until the page goes; a second one, or any other error, rejects.
 */
export function esChunkImport( pImport ) {
    return () => pImport().catch( ( pError ) => {
        if ( isChunkLoadError( pError ) && reloadOnce() ) return new Promise( () => {} );
        throw pError;
    } );
}

/** The friendly half: escapade was updated, reload. */
export function EsChunkUpdated() {
    const t = useEsStrings();
    return (
        <section data-testid="es-chunk-updated" className="relative z-10 pb-4 pt-nav-room">
            { /* One centered column with even steps between heading, sentence, and button. Not the
                 error page's red: a new version is news, not a failure (Tim, 2026-10-22). */ }
            <div className="container flex flex-col items-center gap-6 py-16 text-center">
                <p data-testid="es-chunk-updated-heading" className="errorPageHeading mb-0">
                    { t( 'common-error-updated', 'Escapade was updated' ) }
                </p>
                <p data-testid="es-chunk-updated-message" className="bodyText mb-0 max-w-md text-muted">
                    { t( 'common-error-updated-message', 'A newer version is out. Reload to open this page.' ) }
                </p>
                <Button data-testid="es-chunk-updated-reload" type="button" onClick={ () => esChunkPage.reload() }>
                    { t( 'common-error-updated-reload', 'Reload' ) }
                </Button>
            </div>
        </section>
    );
}

/**
 * Catches a chunk-load failure below it and draws EsChunkUpdated in its place, inside the shell.
 * Anything else is thrown on to the next boundary up, which is the router's.
 */
export class EsChunkBoundary extends Component {
    constructor( pProps ) {
        super( pProps );
        this.state = { error: null };
    }

    static getDerivedStateFromError( pError ) {
        return { error: pError };
    }

    render() {
        if ( !this.state.error ) return this.props.children;
        if ( !isChunkLoadError( this.state.error ) ) throw this.state.error;
        return <EsChunkUpdated />;
    }
}

/**
 * React's lazy() for a routed page: the import guarded as above, under its own Suspense and
 * EsChunkBoundary, so no route can render one outside a boundary. The fallback is nothing: the
 * Suspense sits inside EsParent's Outlet, so the nav and the shell stay drawn while the page
 * loads, and a navigation (a transition under react-router 7) keeps the page it left until then.
 */
export function esLazy( pImport ) {
    const Page = lazy( esChunkImport( pImport ) );
    return function EsLazyPage( pProps ) {
        return (
            <EsChunkBoundary>
                <Suspense fallback={ null }>
                    <Page { ...pProps } />
                </Suspense>
            </EsChunkBoundary>
        );
    };
}
